import React, { useState, useRef } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import BaseModal from 'components/v2/Modals/BaseModal';
import { useCreateClient } from 'mutations/client';
import { useUser } from 'common/hooks/user';
import { useHistory } from 'react-router-dom';
import Tabs from 'components/v2/Tabs';
import Tab from 'components/v2/Tab';

import TabPanel from 'components/TabPanel';
import ClientSelector from 'components/v2/ClientSelector';
import NewClientForm from 'components/v2/Forms/NewClientForm';
import AsyncButton from 'components/v2/AsyncButton';
import Button from 'components/v2/Button';

/**
 * @todo: this is a ghetto layer on top of an existing modal, we should really properly clean this up at some point!
 */
export default NiceModal.create(function CreateAddClientModal(props) {
  const {
    client: initialClient,
    showSelect = true,
    showCreate = true,
    title = 'Select Client',
    subtext = 'Choose an existing client or create a new one.',
    selectClientButtonText = 'Select Client',
    createClientButtonText = 'Create Client',
    invertTabs = false,
    canDelete,
    onDelete,
    onNewClient,
    onSelectExistingClient,
    loading = false,
    agencyId,
    labelledById,
    showCurrentClient = false,
  } = props;

  const user = useUser();
  const createClient = useCreateClient();
  const modal = useModal();
  const history = useHistory();

  const [activeTab, setActiveTab] = useState(0);
  const [selectedClient, setSelectedClient] = useState(initialClient);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreateFormValid, setIsCreateFormValid] = useState(false);
  const formRef = useRef();
  const isLoading = loading || isSubmitting;

  // Submit new-client form
  const handleNewClientSubmit = data => {
    setIsSubmitting(true);
    createClient.mutate(
      { ...data, agencyId },
      {
        onSuccess: async client => {
          await user.updateClient(client);
          if (onNewClient) {
            onNewClient(client, modal);
          } else {
            modal.hide();
            history.push(`/clients/${client.id}`);
          }
        },
        onError: () => setIsSubmitting(false),
      },
    );
  };

  const handleClose = () => {
    if (isLoading) return;
    modal.hide();
  };

  const handleCancel = () => {
    if (isLoading) return;
    modal.hide();
  };

  // Continue button: either select existing or submit new
  const handleContinue = () => {
    if (tabOrder[activeTab] === 'select') {
      onSelectExistingClient(selectedClient, modal);
    } else if (tabOrder[activeTab] === 'create') {
      formRef.current?.submit();
    }
  };

  // Build tab definitions
  let tabs = [];

  if (showSelect) {
    tabs.push({
      name: 'select',
      label: 'Select Existing',
      // @todo: does showCurrentClient go away?
      component: () => (
        <ClientSelector selectedClient={selectedClient} onSelect={setSelectedClient} idKey="id" showCurrentClient={showCurrentClient} />
      ),
    });
  }

  if (showCreate) {
    tabs.push({
      name: 'create',
      label: 'Create New',
      component: () => (
        <NewClientForm
          ref={formRef}
          onSubmit={handleNewClientSubmit}
          agencyId={agencyId}
          disabled={isLoading}
          onValidChange={setIsCreateFormValid}
        />
      ),
    });
  }

  if (invertTabs) {
    tabs = tabs.reverse();
  }

  const tabOrder = tabs.map(tab => tab.name);

  // Disable logic for the Continue button
  const disableContinue = tabOrder[activeTab] === 'select' ? !selectedClient : !isCreateFormValid || isLoading; // on create tab, only disable while submitting or invalid form

  return (
    <BaseModal
      title={title}
      subtext={subtext}
      onClose={handleClose}
      onCancel={handleCancel}
      loading={isLoading}
      labelledById={labelledById}
      controls={
        <>
          {tabOrder[activeTab] === 'select' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {canDelete && (
                  <AsyncButton variant="outlined" color="secondary" onClick={() => onDelete(selectedClient, modal)}>
                    Deselect Client
                  </AsyncButton>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="outlined" onClick={handleCancel}>
                  Cancel
                </Button>
                <AsyncButton variant="contained" onClick={handleContinue} disabled={disableContinue}>
                  {selectClientButtonText}
                </AsyncButton>
              </div>
            </div>
          )}
          {tabOrder[activeTab] === 'create' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
              <AsyncButton variant="contained" onClick={handleContinue} disabled={disableContinue} loading={isLoading}>
                {createClientButtonText}
              </AsyncButton>
            </div>
          )}
        </>
      }
      maxWidth="md"
      fullWidth
    >
      <Tabs value={activeTab} onChange={(_e, idx) => setActiveTab(idx)}>
        {tabs.map(tab => (
          <Tab key={tab.name} label={tab.label} />
        ))}
      </Tabs>
      {tabs.map((tab, index) => (
        <TabPanel key={tab.name} value={activeTab} index={index}>
          {tab.component()}
        </TabPanel>
      ))}
    </BaseModal>
  );
});
